import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class, School, Student, Teacher } from "../models";
import { PROFILE_APPROVAL_STATUS } from "../models/constants/model-constants";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
  };
}

class AdminController {
  /**
   * Get all schools across all schools with advanced filtering and pagination
   * @route GET /api/v1/admin/schools
   * @access Private (Admin/Super Admin)
   */
  async getAllSchools(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        status,
        verificationStatus,
        search,
        page = 1,
        limit = 20,
        sortBy = "createdAt",
        sortOrder = "desc",
        isVerified,
      } = req.query;

      // Build query
      const query: any = {};

      if (status) query.status = status;
      if (verificationStatus) query.verificationStatus = verificationStatus;
      if (isVerified !== undefined) {
        query.isVerified = isVerified === "true";
      }

      // Search filter
      if (search) {
        query.$or = [
          { schoolName: { $regex: search, $options: "i" } },
          { schoolCode: { $regex: search, $options: "i" } },
          { email: { $regex: search, $options: "i" } },
        ];
      }

      // Build sort object
      const sort: any = {};
      const validSortFields = [
        "createdAt",
        "schoolName",
        "status",
        "verificationStatus",
        "schoolCode",
      ];
      const sortField = validSortFields.includes(sortBy as string)
        ? (sortBy as string)
        : "createdAt";
      sort[sortField] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      );
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [schools, totalCount] = await Promise.all([
        School.find({ ...query })
          .populate("userId", "email isEmailVerified status")
          .populate("verifiedBy", "email")
          .sort(sort)
          .skip(skip)
          .limit(limitNum)
          .lean(),
        School.countDocuments({ ...query }),
      ]);

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasNextPage = pageNum < totalPages;
      const hasPrevPage = pageNum > 1;

      res.status(200).json({
        success: true,
        message: `${totalCount} schools found`,
        data: {
          schools: schools.map(school => ({
            id: school._id,
            schoolCode: school.schoolCode,
            schoolName: school.schoolName,
            email: school.email,
            phone: school.phone,
            establishmentYear: school.establishmentYear,
            address: school.address,
            status: school.status,
            verificationStatus: school.verificationStatus,
            isVerified: school.isVerified,
            verifiedAt: school.verifiedAt,
            verifiedBy: school.verifiedBy,
            rejectionReason: school.rejectionReason,
            totalStudents: school.totalStudents,
            totalTeachers: school.totalTeachers,
            totalClasses: school.totalClasses,
            createdAt: school.createdAt,
            updatedAt: school.updatedAt,
          })),
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasNextPage,
            hasPrevPage,
            nextPage: hasNextPage ? pageNum + 1 : null,
            prevPage: hasPrevPage ? pageNum - 1 : null,
          },
          filters: {
            status: status || null,
            verificationStatus: verificationStatus || null,
            isVerified: isVerified || null,
            search: search || null,
          },
          sort: {
            field: sortField,
            order: sortOrder,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching all schools (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch schools",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Approve school verification
   * @route PUT /api/v1/admin/schools/:id/approve
   * @access Private (Admin/Super Admin)
   */
  async approveSchool(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const adminUserId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid school ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const school = await School.findById(id);

      if (!school) {
        res.status(404).json({
          success: false,
          message: "School not found",
          error: "School not found",
        } as APIResponse);
        return;
      }

      // Approve the school
      await (school as any).approve(new mongoose.Types.ObjectId(adminUserId));

      logger.info(`School approved by admin: ${school.schoolName}`, {
        schoolId: id,
        adminUserId,
      });

      res.status(200).json({
        success: true,
        message: "School approved successfully",
        data: {
          school: {
            id: school._id,
            schoolCode: school.schoolCode,
            schoolName: school.schoolName,
            status: school.status,
            verificationStatus: school.verificationStatus,
            isVerified: school.isVerified,
            verifiedAt: school.verifiedAt,
            verifiedBy: school.verifiedBy,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error approving school:", error);
      res.status(500).json({
        success: false,
        message: "Failed to approve school",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Reject school verification
   * @route PUT /api/v1/admin/schools/:id/reject
   * @access Private (Admin/Super Admin)
   */
  async rejectSchool(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { reason } = req.body;
      const adminUserId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid school ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!reason || typeof reason !== "string" || reason.trim().length === 0) {
        res.status(400).json({
          success: false,
          message: "Rejection reason is required",
          error: "Missing rejection reason",
        } as APIResponse);
        return;
      }

      const school = await School.findById(id);

      if (!school) {
        res.status(404).json({
          success: false,
          message: "School not found",
          error: "School not found",
        } as APIResponse);
        return;
      }

      // Reject the school
      await (school as any).reject(
        reason,
        new mongoose.Types.ObjectId(adminUserId),
      );

      logger.info(`School rejected by admin: ${school.schoolName}`, {
        schoolId: id,
        adminUserId,
        reason,
      });

      res.status(200).json({
        success: true,
        message: "School rejected successfully",
        data: {
          school: {
            id: school._id,
            schoolCode: school.schoolCode,
            schoolName: school.schoolName,
            status: school.status,
            verificationStatus: school.verificationStatus,
            isVerified: school.isVerified,
            rejectionReason: school.rejectionReason,
            verifiedAt: school.verifiedAt,
            verifiedBy: school.verifiedBy,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error rejecting school:", error);
      res.status(500).json({
        success: false,
        message: "Failed to reject school",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get school details by ID (admin view)
   * @route GET /api/v1/admin/schools/:id
   * @access Private (Admin/Super Admin)
   */
  async getSchoolById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid school ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const school = await School.findById(id)
        .populate("userId", "email isEmailVerified status createdAt")
        .populate("verifiedBy", "email");

      if (!school) {
        res.status(404).json({
          success: false,
          message: "School not found",
          error: "School not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "School found",
        data: {
          school: {
            id: school._id,
            schoolCode: school.schoolCode,
            schoolName: school.schoolName,
            establishmentYear: school.establishmentYear,
            description: school.description,
            phone: school.phone,
            website: school.website,
            email: school.email,
            address: school.address,
            primaryContact: school.primaryContact,
            logo: school.logo,
            documents: school.documents,
            status: school.status,
            verificationStatus: school.verificationStatus,
            isVerified: school.isVerified,
            verifiedAt: school.verifiedAt,
            verifiedBy: school.verifiedBy,
            rejectionReason: school.rejectionReason,
            totalStudents: school.totalStudents,
            totalTeachers: school.totalTeachers,
            totalClasses: school.totalClasses,
            accreditationInfo: school.accreditationInfo,
            socialLinks: school.socialLinks,
            settings: school.settings,
            user: school.userId,
            createdAt: school.createdAt,
            updatedAt: school.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch school",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all teachers across all schools with advanced filtering and pagination
   * @route GET /api/v1/admin/teachers
   * @access Private (Admin)
   * @query approvalStatus, status, subject, search, page, limit, sortBy, sortOrder, dateFrom, dateTo, schoolId, hasDocuments, isEmailVerified
   */
  async getAllTeachers(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        approvalStatus,
        status,
        subject,
        search,
        page = 1,
        limit = 20,
        sortBy = "createdAt",
        sortOrder = "desc",
        dateFrom,
        dateTo,
        schoolId,
        hasDocuments,
        isEmailVerified,
      } = req.query;

      // Build query
      const query: any = {};

      // Add optional filters
      if (approvalStatus) {
        query.approvalStatus = approvalStatus;
      }
      if (status) query.status = status;
      if (subject) query["subjects.name"] = { $regex: subject, $options: "i" };
      if (schoolId) {
        // support comma-separated list or array of ids
        const ids = Array.isArray(schoolId)
          ? schoolId.map(String)
          : (schoolId as string)
              .split(",")
              .map((s: string) => s.trim())
              .filter(Boolean);

        const objectIds: any[] = [];
        for (const id of ids) {
          const idStr = id;
          if (mongoose.Types.ObjectId.isValid(idStr)) {
            objectIds.push(mongoose.Types.ObjectId.createFromHexString(idStr));
          } else {
            res.status(400).json({
              success: false,
              message: "Invalid school ID format",
              error: "Invalid school ID",
            } as APIResponse);
            return;
          }
        }

        if (objectIds.length === 1) {
          query.schoolId = objectIds[0];
        } else if (objectIds.length > 1) {
          query.schoolId = { $in: objectIds };
        }
      }
      if (hasDocuments !== undefined) {
        if (hasDocuments === "true") {
          query.documents = { $exists: true, $not: { $size: 0 } };
        } else {
          query.$or = [
            { documents: { $exists: false } },
            { documents: { $size: 0 } },
          ];
        }
      }

      // Date range filter
      if (dateFrom || dateTo) {
        query.createdAt = {};
        if (dateFrom) {
          query.createdAt.$gte = new Date(dateFrom as string);
        }
        if (dateTo) {
          query.createdAt.$lte = new Date(dateTo as string);
        }
      }

      // Search filter
      if (search) {
        query.$or = [
          { fullName: { $regex: search, $options: "i" } },
          { employeeId: { $regex: search, $options: "i" } },
          { email: { $regex: search, $options: "i" } },
          { phone: { $regex: search, $options: "i" } },
        ];
      }

      // Build sort object
      const sort: any = {};
      const validSortFields = [
        "createdAt",
        "fullName",
        "employeeId",
        "approvalStatus",
        "status",
      ];
      const sortField = validSortFields.includes(sortBy as string)
        ? (sortBy as string)
        : "createdAt";
      sort[sortField] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      ); // Max 100 per page
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [teachers, totalCount, statusCounts, schoolCounts] =
        await Promise.all([
          Teacher.find({ ...query })
            .populate(
              "schoolId",
              "schoolName establishmentYear status isVerified",
            )
            .populate("userId", "email isEmailVerified status")
            .sort(sort)
            .skip(skip)
            .limit(limitNum)
            .lean(), // Use lean() for better performance
          Teacher.countDocuments(query),
          // Get counts for all approval statuses
          Teacher.aggregate([
            { $match: query },
            {
              $group: {
                _id: "$approvalStatus",
                count: { $sum: 1 },
              },
            },
          ]),
          // Get counts by school
          Teacher.aggregate([
            { $match: query },
            {
              $group: {
                _id: "$schoolId",
                count: { $sum: 1 },
              },
            },
            {
              $lookup: {
                from: "schools",
                localField: "_id",
                foreignField: "_id",
                as: "school",
              },
            },
            {
              $unwind: "$school",
            },
            {
              $project: {
                schoolName: "$school.schoolName",
                count: 1,
              },
            },
            { $sort: { count: -1 } },
            { $limit: 10 },
          ]),
        ]);

      // Format status counts
      const statusCountMap: Record<string, number> = {};
      for (const item of statusCounts) {
        statusCountMap[item._id] = item.count;
      }

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasNextPage = pageNum < totalPages;
      const hasPrevPage = pageNum > 1;

      // Format response data
      const formattedTeachers = teachers.map(teacher => ({
        id: teacher._id,
        employeeId: teacher.employeeId,
        fullName: teacher.fullName,
        email: (teacher.userId as any)?.email,
        phone: teacher.phone,
        profilePicture: teacher.profilePicture,
        subjects: teacher.subjects,
        education: teacher.education,
        experience: teacher.experience,
        documents: teacher.documents,
        status: teacher.status,
        approvalStatus: teacher.approvalStatus,
        isApproved: teacher.isApproved,
        approvedAt: teacher.approvedAt,
        approvedBy: teacher.approvedBy,
        rejectionReason: teacher.rejectionReason,
        createdAt: teacher.createdAt,
        updatedAt: teacher.updatedAt,
        isEmailVerified: (teacher.userId as any)?.isEmailVerified,
        hasDocuments: teacher.documents && teacher.documents.length > 0,
        school: {
          id: teacher.schoolId?._id,
          schoolName: (teacher.schoolId as any)?.schoolName,
          establishmentYear: (teacher.schoolId as any)?.establishmentYear,
          status: (teacher.schoolId as any)?.status,
          isVerified: (teacher.schoolId as any)?.isVerified,
        },
      }));

      res.status(200).json({
        success: true,
        message: `${totalCount} teachers found`,
        data: {
          teachers: formattedTeachers,
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasNextPage,
            hasPrevPage,
            nextPage: hasNextPage ? pageNum + 1 : null,
            prevPage: hasPrevPage ? pageNum - 1 : null,
          },
          filters: {
            approvalStatus: approvalStatus || null,
            status: status || null,
            subject: subject || null,
            search: search || null,
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            schoolId: schoolId || null,
            hasDocuments: hasDocuments || null,
            isEmailVerified: isEmailVerified || null,
          },
          statistics: {
            totalTeachers: totalCount,
            approvalStatusBreakdown: {
              pending: statusCountMap.pending || 0,
              approved: statusCountMap.approved || 0,
              rejected: statusCountMap.rejected || 0,
              cancelled: statusCountMap.cancelled || 0,
            },
            schoolBreakdown: schoolCounts,
          },
          sort: {
            field: sortField,
            order: sortOrder,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching all teachers (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teacher statistics across all schools
   * @route GET /api/v1/admin/teachers/statistics
   * @access Private (Admin)
   */
  async getTeacherStatistics(req: AuthRequest, res: Response): Promise<void> {
    try {
      // Get comprehensive statistics
      const [
        totalTeachers,
        statusBreakdown,
        approvalStatusBreakdown,
        schoolBreakdown,
        subjectDistribution,
        recentTeachers,
      ] = await Promise.all([
        Teacher.countDocuments(),
        Teacher.aggregate([
          {
            $group: {
              _id: "$status",
              count: { $sum: 1 },
            },
          },
        ]),
        Teacher.aggregate([
          {
            $group: {
              _id: "$approvalStatus",
              count: { $sum: 1 },
            },
          },
        ]),
        Teacher.aggregate([
          {
            $group: {
              _id: "$schoolId",
              count: { $sum: 1 },
            },
          },
          {
            $lookup: {
              from: "schools",
              localField: "_id",
              foreignField: "_id",
              as: "school",
            },
          },
          {
            $unwind: "$school",
          },
          {
            $project: {
              schoolName: "$school.schoolName",
              count: 1,
            },
          },
          { $sort: { count: -1 } },
        ]),
        Teacher.aggregate([
          { $unwind: "$subjects" },
          {
            $group: {
              _id: "$subjects.name",
              count: { $sum: 1 },
            },
          },
          { $sort: { count: -1 } },
          { $limit: 10 },
        ]),
        Teacher.find()
          .populate("schoolId", "schoolName")
          .sort({ createdAt: -1 })
          .limit(10)
          .select(
            "fullName employeeId approvalStatus status createdAt schoolId",
          )
          .lean(),
      ]);

      // Format breakdowns
      const statusMap: Record<string, number> = {};
      for (const item of statusBreakdown) {
        statusMap[item._id] = item.count;
      }

      const approvalMap: Record<string, number> = {};
      for (const item of approvalStatusBreakdown) {
        approvalMap[item._id] = item.count;
      }

      res.status(200).json({
        success: true,
        message: "Teacher statistics retrieved successfully",
        data: {
          overview: {
            totalTeachers,
            totalSchools: schoolBreakdown.length,
          },
          statusBreakdown: {
            active: statusMap.active || 0,
            inactive: statusMap.inactive || 0,
            on_leave: statusMap.on_leave || 0,
            terminated: statusMap.terminated || 0,
          },
          approvalStatusBreakdown: {
            pending: approvalMap.pending || 0,
            approved: approvalMap.approved || 0,
            rejected: approvalMap.rejected || 0,
            cancelled: approvalMap.cancelled || 0,
          },
          schoolBreakdown: schoolBreakdown.slice(0, 10), // Top 10 schools
          subjectDistribution: subjectDistribution.slice(0, 10), // Top 10 subjects
          recentTeachers: recentTeachers.map(teacher => ({
            id: teacher._id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            approvalStatus: teacher.approvalStatus,
            status: teacher.status,
            schoolName: (teacher.schoolId as any)?.schoolName,
            createdAt: teacher.createdAt,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher statistics (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teacher statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teacher by ID with full details (admin view)
   * @route GET /api/v1/admin/teachers/:id
   * @access Private (Admin)
   */
  async getTeacherById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findById(id)
        .populate(
          "schoolId",
          "schoolName establishmentYear status isVerified address",
        )
        .populate("userId", "email isEmailVerified status createdAt")
        .populate("approvedBy", "fullName email");

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Get teacher's classes
      const classes = await Class.find({
        teacherId: teacher._id,
      })
        .populate("semester", "semesterName academicYear")
        .populate("schoolId", "schoolName")
        .select(
          "className classCode subject status currentStudents maxStudents schoolId semesterId",
        )
        .lean();

      res.status(200).json({
        success: true,
        message: "Teacher found",
        data: {
          teacher: {
            id: teacher._id,
            userId: teacher.userId,
            schoolId: teacher.schoolId,
            employeeId: teacher.employeeId,
            fullName: teacher.fullName,
            dateOfBirth: teacher.dateOfBirth,
            age: (teacher as any).age,
            gender: teacher.gender,
            phone: teacher.phone,
            address: teacher.address,
            profilePicture: teacher.profilePicture,
            bio: teacher.bio,
            status: teacher.status,
            hireDate: teacher.hireDate,
            terminationDate: teacher.terminationDate,
            terminationReason: teacher.terminationReason,
            approvalStatus: teacher.approvalStatus,
            isApproved: teacher.isApproved,
            approvedAt: teacher.approvedAt,
            approvedBy: (teacher as any).approvedBy,
            rejectionReason: teacher.rejectionReason,
            subjects: teacher.subjects,
            education: teacher.education,
            experience: teacher.experience,
            totalExperience: (teacher as any).totalExperience,
            documents: teacher.documents,
            emergencyContact: teacher.emergencyContact,
            performanceMetrics: teacher.performanceMetrics,
            preferences: teacher.preferences,
            school: (teacher as any).schoolId,
            user: (teacher as any).userId,
            classes: classes.map(c => ({
              id: c._id,
              className: c.className,
              classCode: c.classCode,
              subject: c.subject,
              status: c.status,
              currentStudents: c.currentStudents,
              maxStudents: c.maxStudents,
              school: c.schoolId,
              semester: c.semesterId,
            })),
            createdAt: teacher.createdAt,
            updatedAt: teacher.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all students across all schools with filtering and pagination
   * @route GET /api/v1/admin/students
   * @access Private (Admin)
   */
  async getAllStudents(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        status,
        academicLevel,
        academicYear,
        search,
        page = 1,
        limit = 20,
        sortBy = "createdAt",
        sortOrder = "desc",
        dateFrom,
        dateTo,
        schoolId,
        isApproved,
      } = req.query as any;

      const query: any = {};
      if (status) query.status = status;
      if (academicLevel) query.academicLevel = academicLevel;
      if (academicYear) query.academicYear = academicYear;

      if (isApproved !== undefined) {
        query.isApproved = isApproved === "true";
      }

      if (schoolId) {
        if (mongoose.Types.ObjectId.isValid(schoolId as string)) {
          query.schoolId = new mongoose.Types.ObjectId(schoolId as string);
        } else {
          res.status(400).json({
            success: false,
            message: "Invalid school ID format",
            error: "Invalid school ID",
          } as APIResponse);
          return;
        }
      }

      // Date range filter
      if (dateFrom || dateTo) {
        query.createdAt = {} as any;
        if (dateFrom) query.createdAt.$gte = new Date(dateFrom as string);
        if (dateTo) query.createdAt.$lte = new Date(dateTo as string);
      }

      // Search filter
      if (search) {
        query.$or = [
          { fullName: { $regex: search, $options: "i" } },
          { studentId: { $regex: search, $options: "i" } },
          { phone: { $regex: search, $options: "i" } },
          { email: { $regex: search, $options: "i" } },
        ];
      }

      // Build sort object
      const sort: any = {};
      const validSortFields = ["createdAt", "fullName", "studentId", "status"];
      const sortField = validSortFields.includes(sortBy as string)
        ? (sortBy as string)
        : "createdAt";
      sort[sortField] = sortOrder === "desc" ? -1 : 1;

      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      );
      const skip = (pageNum - 1) * limitNum;

      const [students, totalCount] = await Promise.all([
        Student.find({ ...query })
          .populate("userId", "email isEmailVerified")
          .populate("schoolId", "schoolName")
          .sort(sort)
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Student.countDocuments({ ...query }),
      ]);

      const formattedStudents = students.map(student => ({
        id: student._id,
        fullName: student.fullName,
        studentId: student.studentId,
        dateOfBirth: student.dateOfBirth,
        gender: student.gender,
        phone: student.phone,
        email: (student.userId as any)?.email,
        isEmailVerified: (student.userId as any)?.isEmailVerified,
        academicLevel: student.academicLevel,
        academicYear: student.academicYear,
        status: student.status,
        isApproved: student.isApproved,
        profilePicture: student.profilePicture,
        school: student.schoolId
          ? {
              id: (student.schoolId as any)?._id,
              name: (student.schoolId as any)?.schoolName,
            }
          : null,
        guardians: student.guardians,
        createdAt: student.createdAt,
        updatedAt: student.updatedAt,
      }));

      const totalPages = Math.ceil(totalCount / limitNum);

      res.status(200).json({
        success: true,
        message: `${totalCount} students found`,
        data: {
          students: formattedStudents,
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasNextPage: pageNum < totalPages,
            hasPrevPage: pageNum > 1,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching all students (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch students",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Approve student (admin)
   * @route PUT /api/v1/admin/students/:id/approve
   * @access Private (Admin)
   */
  async approveStudent(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const adminUserId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findById(id);

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      await (student as any).approve(new mongoose.Types.ObjectId(adminUserId));

      logger.info(`Student approved by admin: ${student.fullName}`, {
        studentId: id,
        adminUserId,
      });

      res.status(200).json({
        success: true,
        message: "Student approved successfully",
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            isApproved: student.isApproved,
            status: student.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error approving student (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to approve student",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Reject student (admin)
   * @route PUT /api/v1/admin/students/:id/reject
   * @access Private (Admin)
   */
  async rejectStudent(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { reason } = req.body;
      const adminUserId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!reason || typeof reason !== "string" || reason.trim().length < 5) {
        res.status(400).json({
          success: false,
          message:
            "Rejection reason is required and must be at least 5 characters",
          error: "Invalid reason",
        } as APIResponse);
        return;
      }

      const student = await Student.findById(id);

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      await (student as any).reject(
        reason,
        new mongoose.Types.ObjectId(adminUserId),
      );

      logger.info(`Student rejected by admin: ${student.fullName}`, {
        studentId: id,
        adminUserId,
        reason,
      });

      res.status(200).json({
        success: true,
        message: "Student rejected successfully",
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            isApproved: student.isApproved,
            status: student.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error rejecting student (admin):", error);
      res.status(500).json({
        success: false,
        message: "Failed to reject student",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

// Export singleton instance
export const adminController = new AdminController();
